import api from "./api";
import { faMoney, formatDate } from "./format";

const pad = (p, id) => `${p}-${String(id).padStart(4, "0")}`;
const F = (path, name, optional = false) => ({ path, name, optional });

const EQ_STATUS = { healthy: "سالم", unusable: "غیرقابل استفاده" };
const REQ_STATUS = {
  pending: "در انتظار تأیید",
  approved: "تأیید شد",
  rejected: "رد شد",
};

/** فایل‌های چندگانه‌ی تجهیزات (equipment | repair | wear) */
const eqFiles = async (type, id, prefix = "") => {
  try {
    const { data } = await api.get(`/equipment-files/${type}/${id}/files`);
    return data.items.map((x) =>
      F(`/equipment-files/${type}/${id}/files/${x.id}`, `${prefix}${x.name}`),
    );
  } catch {
    return [];
  }
};

/* ---------- تعهدنامه ---------- */
export const pledgePrint = (r, meta) => ({
  title: meta?.pledgeKinds?.[r.kind] || "تعهدنامه",
  docNo: pad("PL", r.id),
  meta: [
    ["تاریخ", formatDate(r.pledgeDate)],
    ["فرد متعهد", `${r.firstName} ${r.lastName}`],
    ["شماره ملی", r.nationalId],
    ["موبایل", r.mobile],
    ["نماینده / شناور", r.representative],
    ["نوع تعمیرات / استفاده", r.usageType],
    ["محل", r.location],
    ["مدت", r.duration],
    ["ثبت‌کننده", r.creatorName],
  ],
  load: async () => {
    const files = [];
    const { data } = await api.get(`/pledges/${r.id}/files`);
    if (r.hasFile) files.push(F(`/pledges/${r.id}/file`, "فایل اصلی تعهدنامه"));
    data.items
      .filter((x) => x.category === "pledge")
      .forEach((x) =>
        files.push(F(`/pledges/${r.id}/files/${x.id}`, `تعهدنامه: ${x.name}`)),
      );
    data.items
      .filter((x) => x.category === "intro")
      .forEach((x) =>
        files.push(
          F(`/pledges/${r.id}/files/${x.id}`, `معرفی‌نامه: ${x.name}`),
        ),
      );
    return { files };
  },
});

/* ---------- نامه‌نگاری ---------- */
export const letterPrint = (r, meta) => ({
  title: "نامه",
  docNo: r.letterNo,
  meta: [
    ["شماره نامه", r.letterNo],
    ["تاریخ", formatDate(r.letterDate)],
    ["نوع", meta?.letterDirections?.[r.direction]],
    ["موضوع", r.subject],
    ["فرستنده", r.sender],
    ["گیرنده", r.recipient],
  ],
  blocks: [["متن نامه", r.body]],
  files: r.hasFile ? [F(`/letters/${r.id}/file`, "فایل نامه")] : [],
});

/* ---------- هزینه ---------- */
export const expensePrint = (r) => ({
  title: "سند هزینه",
  docNo: pad("EX", r.id),
  meta: [
    ["تاریخ", formatDate(r.expenseDate)],
    ["شرح هزینه", r.title],
    ["دسته‌بندی", r.category],
    ["مبلغ (ریال)", faMoney(r.amount)],
    ["طرف حساب", r.payee],
    ["شماره سند", r.refNo],
    ["ثبت‌کننده", r.creatorName],
    ["وضعیت", { pending: "در انتظار تأیید", approved: "تأیید شد", rejected: "رد شد" }[r.status]],
  ],
  blocks: [["توضیحات", r.description]],
  files: r.hasFile ? [F(`/expenses/${r.id}/file`, "تصویر فاکتور")] : [],
});

/* ---------- درخواست ---------- */
export const requestPrint = (r, meta) => ({
  title: meta?.requestScopes?.[r.scope] || "درخواست",
  docNo: pad("RQ", r.id),
  meta: [
    ["تاریخ ثبت", formatDate(r.createAt)],
    ["موضوع", meta?.requestKinds?.[r.scope]?.[r.kind] || r.kind],
    ["عنوان", r.title],
    [
      "بازه",
      r.fromDate
        ? `${formatDate(r.fromDate)}${r.toDate ? ` تا ${formatDate(r.toDate)}` : ""}`
        : "—",
    ],
    ["وضعیت", meta?.requestStatus?.[r.status]],
    ["درخواست‌دهنده", r.creatorName],
  ],
  blocks: [
    ["توضیحات", r.description],
    ["پاسخ / توضیح", r.adminNote],
  ],
});

/* ---------- تجهیزات / اموال / انبار سالم ---------- */
export const equipmentPrint = (r) => ({
  title: r.isProperty ? "کارت تجهیزات و اموال" : "کارت تجهیزات",
  docNo: r.code || pad("EQ", r.id),
  meta: [
    ["کد انبار", r.code],
    ["نام تجهیزات", r.name],
    ["نوع", r.kind],
    ["وضعیت", EQ_STATUS[r.status] || r.status],
    ["محل نصب / نگهداری", r.location],
    ["مسئول محل", r.supervisorName],
    ["شماره تماس مسئول", r.supervisorPhone],
    ["انقضا / شارژ بعدی", r.expiryDate ? formatDate(r.expiryDate) : "—"],
    ...(r.inWarehouse ? [["انبار", "انبار سالم"]] : []),
    ...(r.isProperty
      ? [
          ["شماره اموال", r.propertyNo],
          ["امضاکننده / تحویل‌گیرنده", r.signerName],
        ]
      : []),
  ],
  blocks: [["توضیحات و سابقه‌ی تغییرات", r.notes]],
  load: async () => {
    const files = [];
    if (r.hasFile !== false)
      files.push(
        F(
          `/equipment/${r.id}/file`,
          "فایل / تصویر اصلی",
          r.hasFile === undefined,
        ),
      );
    files.push(...(await eqFiles("equipment", r.id)));
    return { files };
  },
});

/* ---------- تعمیرات و نوسازی ---------- */
export const repairPrint = (r) => ({
  title:
    r.kind === "renewal" ? "گزارش نوسازی تجهیزات" : "گزارش تعمیرات تجهیزات",
  docNo: pad("RP", r.id),
  meta: [
    ["کد انبار", r.equipmentCode],
    ["تجهیزات", r.equipmentName],
    ["نوع کار", r.kind === "renewal" ? "نوسازی" : "تعمیرات"],
    ["تاریخ انجام", formatDate(r.repairDate)],
    ["موضوع", r.subject],
    ["ثبت‌کننده", r.creatorName],
    ["وضعیت", REQ_STATUS[r.status]],
    ["تأییدکننده", r.deciderName],
  ],
  blocks: [
    ["شرح تعمیرات انجام‌شده", r.description],
    ["توضیح تأیید / رد", r.decisionNote],
  ],
  load: async () => ({
    files: [
      ...(r.hasFile ? [F(`/equipment-repairs/${r.id}/file`, "فایل اصلی")] : []),
      ...(await eqFiles("repair", r.id)),
    ],
  }),
});

/* ---------- فرسودگی ---------- */
export const wearPrint = (r) => ({
  title: "گزارش فرسودگی تجهیزات",
  docNo: pad("WR", r.id),
  meta: [
    ["کد انبار", r.equipmentCode],
    ["تجهیزات", r.equipmentName],
    ["کد انبار فرسوده", r.wornCode],
    ["تاریخ فرسودگی", formatDate(r.wearDate)],
    ["ثبت‌کننده", r.creatorName],
    ["وضعیت", REQ_STATUS[r.status]],
    ["تأییدکننده", r.deciderName],
  ],
  blocks: [
    ["علت فرسودگی", r.reason],
    ["توضیح تأیید / رد", r.decisionNote],
  ],
  load: async () => ({
    files: [
      ...(r.hasFile ? [F(`/equipment-wear/${r.id}/file`, "فایل اصلی")] : []),
      ...(await eqFiles("wear", r.id)),
    ],
  }),
});

/* ---------- انبار فرسوده ---------- */
export const wornPrint = (r) => ({
  title: "کارت تجهیزات فرسوده",
  docNo: r.code,
  meta: [
    ["کد انبار فرسوده", r.code],
    ["کد انبار تجهیز", r.equipmentCode],
    ["نام تجهیزات", r.name],
    ["نوع", r.kind],
    ["محل قبلی", r.location],
    ["مسئول محل", r.supervisorName],
    ["شماره تماس مسئول", r.supervisorPhone],
    ["تاریخ فرسودگی", formatDate(r.wearDate)],
    ["ثبت‌کننده", r.registeredByName],
    ["تأییدکننده", r.approvedByName],
  ],
  blocks: [
    ["علت فرسودگی", r.reason],
    ["سابقه‌ی تغییرات تجهیز", r.notes],
  ],
  load: async () => {
    const files = [];
    if (r.hasWearFile)
      files.push(F(`/warehouse/worn/${r.id}/file?type=wear`, "فایل فرسودگی"));
    if (r.hasPhoto)
      files.push(F(`/warehouse/worn/${r.id}/file?type=photo`, "عکس تجهیزات"));
    if (r.equipmentId)
      files.push(...(await eqFiles("equipment", r.equipmentId)));
    return { files };
  },
});

/* ---------- ابلاغ ---------- */
export const directivePrint = (r) => {
  const recips = r.recipients || [];
  const done = recips.filter((x) => x.status === "done");
  const LBL = {
    pending: "در انتظار",
    in_progress: "در حال انجام",
    done: "انجام شد",
    cancelled: "غیرفعال",
  };
  return {
    title: "ابلاغ مدیر بندر",
    docNo: pad("DR", r.id),
    meta: [
      ["موضوع", r.subject],
      ["مهلت انجام", formatDate(r.deadline)],
      ...(recips.length
        ? [
            [
              "نوع گیرنده",
              r.targetType === "shift" ? "شیفت کاری" : "کاربر خاص",
            ],
            ["گیرندگان", recips.map((x) => x.name).join("، ")],
          ]
        : [["وضعیت من", LBL[r.myStatus]]]),
    ],
    blocks: [
      ["شرح ابلاغ", r.description],
      ...done.map((x) => [`گزارش انجام — ${x.name}`, x.report]),
      ...(r.myReport ? [["گزارش من", r.myReport]] : []),
    ],
    tables: recips.length
      ? [
          {
            title: "وضعیت گیرندگان",
            head: ["نام", "وضعیت"],
            rows: recips.map((x) => [x.name, LBL[x.status]]),
          },
        ]
      : [],
    load: async () => {
      const files = [];
      if (r.hasFile)
        files.push(F(`/directives/${r.id}/file`, "پیوست اصلی ابلاغ"));
      const { data } = await api.get(`/directives/${r.id}/files`);
      data.items.forEach((x) =>
        files.push(
          F(
            `/directives/${r.id}/files/${x.id}`,
            `${x.category === "report" ? "گزارش انجام" : "پیوست ابلاغ"}: ${x.name}`,
          ),
        ),
      );
      return { files };
    },
  };
};

/* ---------- نامه‌های مدیر بندر ---------- */
const MLKIND = {
  manager: "نامه‌ی مدیر بندر",
  reply: "پاسخ مسئول ایمنی",
  supplementary: "نامه‌ی تکمیلی",
  staff_reply: "پاسخ کارکن",
};
export const managerLetterPrint = (r) => ({
  title: MLKIND[r.kind] || "نامه",
  docNo: r.letterNo,
  meta: [
    ["شماره", r.letterNo],
    ["تاریخ", formatDate(r.letterDate)],
    ["نوع", MLKIND[r.kind]],
    ["فرستنده", r.senderName],
    ["موضوع", r.subject],
  ],
  load: async () => {
    const { data } = await api.get(`/manager-letters/${r.id}`);
    const it = data.item;
    const files = [];
    const add = (l, pre) => {
      if (l.hasLegacyFile)
        files.push(F(`/manager-letters/${l.id}/file`, `${pre}پیوست اصلی`));
      (l.files || []).forEach((f) =>
        files.push(
          F(`/manager-letters/${l.id}/files/${f.id}`, `${pre}${f.name}`),
        ),
      );
    };
    add(it, "");
    if (it.parent) add(it.parent, "نامه‌ی پیوست: ");
    return {
      meta: [
        ["شماره", it.letterNo],
        ["تاریخ", formatDate(it.letterDate)],
        ["نوع", MLKIND[it.kind]],
        ["فرستنده", it.senderName],
        ["موضوع", it.subject],
        ...(it.recipients?.length
          ? [["گیرندگان", it.recipients.map((x) => x.name).join("، ")]]
          : []),
      ],
      blocks: [
        ["شرح نامه", it.body],
        ...(it.parent
          ? [
              [
                `نامه‌ی پیوست ${it.parent.letterNo} (${formatDate(it.parent.letterDate)}) — ${it.parent.subject}`,
                it.parent.body,
              ],
            ]
          : []),
      ],
      files,
    };
  },
});

/* ---------- ماموریت ---------- */
export const missionPrint = (r) => {
  const ST = {
    pending: "شروع نشده",
    in_progress: "در حال انجام",
    done: "انجام شد",
  };
  return {
    title: "گزارش ماموریت",
    docNo: pad("MS", r.id),
    meta: [
      ["موضوع", r.title],
      ["نوع ماموریت", r.missionTypeLabel],
      ["محل", r.location],
      [
        "بازه",
        `${formatDate(r.missionDate)} تا ${formatDate(r.toDate || r.missionDate)}`,
      ],
    ],
    blocks: [["شرح ماموریت", r.description]],
    tables: [
      {
        title: "اعضا و گزارش‌ها",
        head: ["نام", "وضعیت", "گزارش"],
        rows: (r.employees || []).map((e) => [
          e.name,
          e.overdue ? "انجام نشد (گذشته)" : ST[e.status],
          e.report || "—",
        ]),
      },
    ],
  };
};

/* ---------- وظیفه‌ی روزانه ---------- */
export const taskPrint = (t, meta) => ({
  title: "گزارش وظیفه‌ی روزانه",
  docNo: pad("TK", t.id),
  meta: [
    ["وظیفه", t.title],
    ["تاریخ", formatDate(t.taskDate)],
    ["کارمند", t.employeeName],
    ["وضعیت", meta?.taskStatus?.[t.status]],
  ],
  load: async () => {
    const { data } = await api.get(`/daily-tasks/${t.id}/report`);
    return {
      blocks: [["شرح اقدامات انجام‌شده", data.report]],
      tables: data.checklist?.length
        ? [
            {
              title: "چک‌لیست",
              head: ["مورد", "انجام شد", "توضیحات"],
              rows: data.checklist.map((c) => [
                c.title,
                c.checked ? "✓" : "✗",
                c.note || "—",
              ]),
            },
          ]
        : [],
      files: (data.files || []).map((f) =>
        F(`/daily-tasks/${t.id}/files/${f.id}`, f.name),
      ),
    };
  },
});

/* ---------- تخلف ---------- */
export const violationPrint = (r) => ({
  title: "گزارش تخلف",
  docNo: pad("VL", r.id),
  meta: [
    ["تاریخ", formatDate(r.violationDate)],
    ["موضوع تخلف", r.subject],
    ["محل انجام تخلف", r.location],
    ["ثبت‌کننده", r.creatorName],
    [
      "وضعیت",
      r.forwarded
        ? "ارسال‌شده به مدیر بندر"
        : r.sentToAdmin
          ? "ارسال‌شده به مسئول ایمنی"
          : "ارسال‌نشده",
    ],
  ],
  blocks: [
    ["شرح تخلف", r.description],
    ["اقدامات انجام شده", r.actions],
  ],
  load: async () => {
    const { data } = await api.get(`/violations/${r.id}/files`);
    return {
      files: data.items.map((x) =>
        F(`/violations/${r.id}/files/${x.id}`, x.name),
      ),
    };
  },
});

